import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { ScanLine } from 'lucide-react'
import { useHouseholdId } from '../hooks/useHouseholdId'
import { useUnpackList } from '../hooks/useUnpackList'
import { useUnpackListById } from '../hooks/useUnpackListById'
import { useFinishUnpack } from '../hooks/useFinishUnpack'
import { useAuth } from '../hooks/useAuth'
import { usePreferences } from '../hooks/usePreferences'
import { UnpackMasthead } from '../components/unpack/UnpackMasthead'
import { UnpackItemRow, type UnpackRowState } from '../components/unpack/UnpackItemRow'
import { ImpulseBuyInput } from '../components/unpack/ImpulseBuyInput'
import { TotalStoreInput } from '../components/unpack/TotalStoreInput'
import ReceiptScanner, { type ConfirmedReceiptItem } from '../components/unpack/ReceiptScanner'
import { showToast } from '../lib/toastStore'
import { defaultsForCategory, suggestedExpiryDate, type Location } from '../lib/itemDefaults'
import { checkLimit, FREE_LIMITS } from '../lib/usageLimits'
import { debugLog } from '../lib/debugLogger'

type PendingImpulse = {
  tempId:        string
  name:          string
  category:      string
  confirmed:     boolean
  quantity?:     number | null
  quantityUnit?: string
  price?:        number | null
  location:      Location
  tracksExpiry:  boolean
  expiryDate:    string | null
}

type NavState = {
  fromShopping?:    boolean
  purchasedItemIds?: string[]  // pantry_items.id values
} | null

export default function Unpack() {
  const navigate = useNavigate()
  const location = useLocation()
  const navState = (location.state as NavState) ?? null

  // mode='spontaneous' enters the page with a pre-created shopping_lists row
  // whose id is on the query string. The list has no list_items yet — items
  // get added via receipt scan / impulse input. mode='list' (default)
  // preserves the existing planned-trip behavior: useUnpackList finds the
  // most recent uncompleted list for the household.
  const [searchParams] = useSearchParams()
  const mode          = searchParams.get('mode') === 'spontaneous' ? 'spontaneous' : 'list'
  const listIdParam   = searchParams.get('listId')
  // Set when the user enters via the Home card's secondary "Add manually"
  // link — they explicitly want to bypass the camera and go straight to the
  // empty unpack screen.
  const skipScan      = searchParams.get('skipScan') === '1'

  const householdId                         = useHouseholdId()
  // Pass null to whichever hook isn't active so it short-circuits its query.
  const fromList = useUnpackList(mode === 'list' ? householdId : null)
  const fromId   = useUnpackListById(mode === 'spontaneous' ? listIdParam : null)
  const { list, items, loading, refresh }   = mode === 'spontaneous' ? fromId : fromList
  const { finish, loading: finishing }      = useFinishUnpack()
  const { user }                            = useAuth()
  const { isPremium }                       = usePreferences()

  // User edits to per-item state. Anything missing here falls back to the
  // baseline derived from the items array (see baselineState below).
  const [byId,        setById]        = useState<Record<string, UnpackRowState>>({})
  const [impulses,    setImpulses]    = useState<PendingImpulse[]>([])
  const [totalSpent,  setTotalSpent]  = useState<number | null>(null)
  const [storeName,   setStoreName]   = useState<string | null>(null)
  const [showScanner, setShowScanner] = useState(false)
  const [autoOpened,  setAutoOpened]  = useState(false)

  // Spontaneous mode: the user pressed "Just got back?" on Home — they want
  // to scan, not stare at an empty list. Auto-open the scanner once the list
  // has loaded. Latch via autoOpened so we don't reopen if the user closes it.
  // skipScan=1 lets the secondary "Add manually" link bypass the auto-open.
  useEffect(() => {
    if (mode === 'spontaneous' && !loading && list && !autoOpened && !skipScan) {
      setShowScanner(true)
      setAutoOpened(true)
    }
  }, [mode, loading, list, autoOpened, skipScan])

  // [Unpack-DEBUG] mount snapshot — confirms what URL params the page
  // observed and whether the spontaneous list resolved. Tells us at a
  // glance whether a Capacitor process restart wiped the navigation.
  // Also writes to debug_logs so we can read it remotely.
  useEffect(() => {
    console.log('[Unpack-DEBUG] mounted', {
      mode,
      listIdParam,
      skipScan,
      hasNavState: !!navState,
    })
    void debugLog({
      feature: 'unpack-page',
      step:    'mounted',
      payload: {
        mode,
        listIdParam,
        skipScan,
        hasNavState: !!navState,
      },
    })
  // mount-only snapshot
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Baseline per-item state derived from the items array — available the
  // SAME render items arrive in. The previous useEffect-based seed left a
  // render gap where rows existed but byId[id] was undefined, which crashed
  // any access to state.location.
  const baselineState = useMemo<Record<string, UnpackRowState>>(() => {
    if (!items.length) return {}
    const purchasedSet = new Set(navState?.purchasedItemIds ?? [])
    const out: Record<string, UnpackRowState> = {}
    for (const it of items) {
      const d = defaultsForCategory(it.category)
      out[it.id] = {
        confirmed:    it.confirmedBought || purchasedSet.has(it.pantryItemId),
        location:     d.location,
        tracksExpiry: d.tracksExpiry,
        expiryDate:   d.tracksExpiry ? suggestedExpiryDate(it.category) : null,
      }
    }
    return out
  // navState is captured at mount; baseline only needs to recompute when items change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items])

  // Effective state for an item: user edits (byId) overlaid on baseline.
  // Always returns a defined object — no caller can crash on undefined access.
  function getState(id: string): UnpackRowState {
    return byId[id] ?? baselineState[id] ?? {
      confirmed:    false,
      location:     'pantry',
      tracksExpiry: false,
      expiryDate:   null,
    }
  }

  const confirmedCount = useMemo(() => {
    const fromList    = items.filter(i => getState(i.id).confirmed).length
    const fromImpulse = impulses.filter(i => i.confirmed).length
    return fromList + fromImpulse
  // getState reads from byId + baselineState; both are deps that matter.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, byId, baselineState, impulses])

  const totalCount = items.length + impulses.length
  const canFinish  = confirmedCount > 0

  function patchItem(id: string, patch: Partial<UnpackRowState>) {
    const cur = getState(id)
    setById(prev => ({ ...prev, [id]: { ...cur, ...patch } }))
  }

  function toggleConfirm(id: string) {
    const cur = getState(id)
    patchItem(id, { confirmed: !cur.confirmed })
  }

  function patchImpulse(tempId: string, patch: Partial<PendingImpulse>) {
    setImpulses(prev => prev.map(i => i.tempId === tempId ? { ...i, ...patch } : i))
  }

  function toggleImpulse(tempId: string) {
    setImpulses(prev => prev.map(i => i.tempId === tempId ? { ...i, confirmed: !i.confirmed } : i))
  }

  function addImpulse(name: string) {
    const tempId   = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
    const category = 'other'
    const d        = defaultsForCategory(category)
    setImpulses(prev => [...prev, {
      tempId,
      name,
      category,
      confirmed:    true,
      location:     d.location,
      tracksExpiry: d.tracksExpiry,
      expiryDate:   d.tracksExpiry ? suggestedExpiryDate(category) : null,
    }])
  }

  // ── Receipt scanner integration ──────────────────────────────────────────────

  async function handleScanTap() {
    if (isPremium) { setShowScanner(true); return }
    if (!user) { setShowScanner(true); return }

    const result = await checkLimit(user.id, isPremium, 'receipt_scans')
    if (result?.hitLimit) {
      showToast(`You've used all ${FREE_LIMITS.receipt_scans} scans this month`, {
        action:     { label: 'Upgrade', onClick: () => navigate('/paywall') },
        durationMs: 5000,
      })
      return
    }
    setShowScanner(true)
  }

  function handleReceiptItemsScanned(
    receiptItems: ConfirmedReceiptItem[],
    meta?: { store?: string; total?: number | null },
  ) {
    console.log('[Unpack] handleReceiptItemsScanned: received', receiptItems.length, 'items, store=', meta?.store ?? '(none)', 'total=', meta?.total ?? null)
    void debugLog({
      feature: 'unpack-page',
      step:    'received-receipt-items',
      payload: { itemsCount: receiptItems.length, store: meta?.store ?? null, total: meta?.total ?? null },
    })
    const store = meta?.store?.trim() || null

    // Build a quick-lookup map of receipt items by normalized name. Each
    // receipt item is consumed at most once — first list match wins.
    const byNormName = new Map<string, ConfirmedReceiptItem>()
    for (const ri of receiptItems) {
      const key = ri.name.trim().toLowerCase()
      if (key && !byNormName.has(key)) byNormName.set(key, ri)
    }

    // Pass 1: for every list item with a name match, mark it confirmed and
    // attach the per-item price + store onto its byId state. Removes that
    // receipt item from the pool so it doesn't also become an impulse.
    const matched = new Set<string>()  // receipt-item keys consumed by list match
    setById(prev => {
      const next = { ...prev }
      for (const li of items) {
        const key = li.name.trim().toLowerCase()
        const ri  = byNormName.get(key)
        if (!ri) continue
        matched.add(key)
        const cur = prev[li.id] ?? baselineState[li.id] ?? {
          confirmed:    false,
          location:     'pantry' as Location,
          tracksExpiry: false,
          expiryDate:   null,
        }
        next[li.id] = {
          ...cur,
          confirmed: true,
          pricePaid: ri.price ?? null,
          storeName: store,
          quantity:  ri.quantity ?? null,
        }
      }
      return next
    })

    // Pass 2: receipt items NOT matched against the list become impulses.
    const unmatched = receiptItems.filter(ri => !matched.has(ri.name.trim().toLowerCase()))
    if (unmatched.length > 0) {
      const seedTime = Date.now()
      setImpulses(prev => [
        ...prev,
        ...unmatched.map((it, idx) => {
          const d            = defaultsForCategory(it.category)
          const tracksExpiry = it.tracks_expiry ?? d.tracksExpiry
          const expiryDate   = it.expiry_date
            ?? (tracksExpiry ? suggestedExpiryDate(it.category) : null)
          return {
            tempId:       `tmp_${seedTime}_${idx}_${Math.random().toString(36).slice(2, 7)}`,
            name:         it.name.trim(),
            category:     it.category,
            confirmed:    true,
            quantity:     it.quantity,
            quantityUnit: it.quantity_unit,
            price:        it.price,
            location:     d.location,
            tracksExpiry,
            expiryDate,
          } satisfies PendingImpulse
        }),
      ])
    }

    if (store && !storeName) setStoreName(store)
    if (typeof meta?.total === 'number' && totalSpent == null) setTotalSpent(meta.total)
    setShowScanner(false)

    const matchedCount = receiptItems.length - unmatched.length
    const parts: string[] = []
    if (matchedCount  > 0) parts.push(`${matchedCount} matched`)
    if (unmatched.length > 0) parts.push(`${unmatched.length} added as impulse`)
    console.log('[Unpack] handoff complete: matched=', matchedCount, 'impulses=', unmatched.length)
    void debugLog({
      feature: 'unpack-page',
      step:    'handoff-complete',
      payload: { matched: matchedCount, impulses: unmatched.length },
    })
    showToast(parts.length ? parts.join(' · ') : 'Nothing added from receipt')
  }

  async function handleFinish() {
    if (!list || !householdId) return

    const ok = await finish({
      shoppingListId: list.id,
      householdId,
      confirmations: items.map(i => {
        const s = getState(i.id)
        return {
          listItemId:   i.id,
          pantryItemId: i.pantryItemId,
          confirmed:    s.confirmed,
          location:     s.location,
          tracksExpiry: s.tracksExpiry,
          expiryDate:   s.tracksExpiry ? (s.expiryDate ?? null) : null,
          pricePaid:    s.pricePaid ?? null,
          storeName:    s.storeName ?? null,
          quantity:     s.quantity ?? null,
        }
      }),
      impulseItems: impulses.filter(i => i.confirmed).map(i => ({
        name:         i.name,
        quantity:     i.quantity,
        quantityUnit: i.quantityUnit,
        price:        i.price ?? null,
        category:     i.category,
        location:     i.location,
        tracksExpiry: i.tracksExpiry,
        expiryDate:   i.tracksExpiry ? i.expiryDate : null,
      })),
      totalSpent,
      storeName,
    })

    if (ok) {
      const putAway = confirmedCount
      refresh()
      showToast(`Put ${putAway} item${putAway === 1 ? '' : 's'} away.`)
      // Spontaneous trips emanate from Home — drop the user back where they
      // started. Planned trips emanate from Shop — same idea.
      navigate(mode === 'spontaneous' ? '/home' : '/shop', { replace: true })
    } else {
      // useFinishUnpack swallows the throw and returns false; surface it so
      // the user knows we didn't move on, and log so silent failures show up
      // in DevTools instead of vanishing.
      console.error('[Unpack] finishUnpack returned false — see prior [useFinishUnpack] logs for the underlying error')
      showToast('Could not finish unpacking. Try again.')
    }
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-cream p-4">
        <p className="font-serif italic text-ink-muted">Loading your list…</p>
      </main>
    )
  }

  if (!list) {
    return (
      <main className="min-h-screen bg-cream px-4 pt-8 pb-6">
        <UnpackMasthead
          confirmedCount={0}
          totalCount={0}
          onBack={() => navigate(-1)}
        />
        <div className="rounded-[18px] border border-dashed border-border-sand bg-paper/60 px-4 py-8 text-center">
          <p className="font-serif text-[15px] italic text-ink">
            <em>Nothing to unpack.</em>
          </p>
          <p className="mt-2 font-serif text-[13px] italic text-ink-muted">
            Start a list from the Shop tab first.
          </p>
        </div>
      </main>
    )
  }

  const scanCard = (
    <button
      onClick={handleScanTap}
      className="mb-5 w-full rounded-[16px] border border-border-sand bg-paper px-4 py-3.5 flex items-center gap-3 text-left transition-colors hover:bg-[#FAF6EE]"
    >
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#E8F0E4]">
        <ScanLine className="h-[18px] w-[18px] text-forest" strokeWidth={1.7} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted">
          Faster unpack
        </p>
        <p className="font-serif text-[14px] italic text-ink leading-snug">
          Scan your receipt to <em className="not-italic font-medium text-forest">auto-fill items</em>
        </p>
      </div>
    </button>
  )

  // Partition items into confirmed / unconfirmed using effective state.
  const sortedItems       = items.slice().sort((a, b) => a.name.localeCompare(b.name))
  const confirmedItems    = sortedItems.filter(i => getState(i.id).confirmed)
  const unconfirmedItems  = sortedItems.filter(i => !getState(i.id).confirmed)
  const confirmedImpulses   = impulses.filter(i => i.confirmed)
  const unconfirmedImpulses = impulses.filter(i => !i.confirmed)

  // Default state for impulse rows so the UnpackRow component reads consistently.
  // The ?? fallbacks are paranoid but cheap — locks in sane defaults if an
  // impulse ever drifts (e.g. older receipt-scan rows missing fields).
  const impulseRowState = (i: PendingImpulse, confirmed: boolean): UnpackRowState => ({
    confirmed,
    location:     i.location ?? 'pantry',
    tracksExpiry: i.tracksExpiry ?? false,
    expiryDate:   i.expiryDate ?? null,
  })

  return (
    <main className="min-h-screen bg-cream px-4 pt-8 pb-6">

      <UnpackMasthead
        confirmedCount={confirmedCount}
        totalCount={totalCount}
        onBack={() => navigate(-1)}
      />

      {scanCard}

      {(confirmedItems.length > 0 || confirmedImpulses.length > 0) && (
        <>
          <SectionHeader
            title="Confirmed"
            count={confirmedItems.length + confirmedImpulses.length}
            tone="forest"
          />
          <div className="mb-5 overflow-hidden rounded-[16px] border border-border-sand bg-paper">
            {confirmedItems.map((item, idx) => (
              <UnpackItemRow
                key={item.id}
                name={item.name}
                category={item.category}
                subtitle={item.categoryLabel}
                isImpulse={item.isImpulse}
                isLast={idx === confirmedItems.length - 1 && confirmedImpulses.length === 0}
                state={getState(item.id)}
                onToggle={() => toggleConfirm(item.id)}
                onPatch={(p) => patchItem(item.id, p)}
              />
            ))}
            {confirmedImpulses.map((item, idx) => (
              <UnpackItemRow
                key={item.tempId}
                name={item.name}
                category={item.category}
                subtitle="Impulse buy"
                isImpulse
                isLast={idx === confirmedImpulses.length - 1}
                state={impulseRowState(item, true)}
                onToggle={() => toggleImpulse(item.tempId)}
                onPatch={(p) => patchImpulse(item.tempId, p)}
              />
            ))}
          </div>
        </>
      )}

      {(unconfirmedItems.length > 0 || unconfirmedImpulses.length > 0) && (
        <>
          <SectionHeader
            title="Still to confirm"
            count={unconfirmedItems.length + unconfirmedImpulses.length}
            tone="terracotta"
          />
          <div className="mb-5 overflow-hidden rounded-[16px] border border-border-sand bg-paper">
            {unconfirmedItems.map((item, idx) => (
              <UnpackItemRow
                key={item.id}
                name={item.name}
                category={item.category}
                subtitle={item.categoryLabel}
                isImpulse={item.isImpulse}
                isLast={idx === unconfirmedItems.length - 1 && unconfirmedImpulses.length === 0}
                state={getState(item.id)}
                onToggle={() => toggleConfirm(item.id)}
                onPatch={(p) => patchItem(item.id, p)}
              />
            ))}
            {unconfirmedImpulses.map((item, idx) => (
              <UnpackItemRow
                key={item.tempId}
                name={item.name}
                category={item.category}
                subtitle="Impulse buy"
                isImpulse
                isLast={idx === unconfirmedImpulses.length - 1}
                state={impulseRowState(item, false)}
                onToggle={() => toggleImpulse(item.tempId)}
                onPatch={(p) => patchImpulse(item.tempId, p)}
              />
            ))}
          </div>
        </>
      )}

      <ImpulseBuyInput onAdd={addImpulse} />

      <TotalStoreInput
        totalSpent={totalSpent}
        storeName={storeName}
        onTotalChange={setTotalSpent}
        onStoreChange={setStoreName}
        onFinish={handleFinish}
        finishing={finishing}
        canFinish={canFinish}
      />

      {showScanner && (
        <ReceiptScanner
          onComplete={handleReceiptItemsScanned}
          onSkip={() => setShowScanner(false)}
          isPremium={isPremium}
          userId={user?.id}
        />
      )}

    </main>
  )
}

function SectionHeader({
  title, count, tone,
}: { title: string; count: number; tone: 'forest' | 'terracotta' }) {
  const color = tone === 'forest' ? 'text-forest' : 'text-terracotta'
  return (
    <div className={`mb-2 flex items-baseline gap-2 ${color}`}>
      <span className="font-serif text-[14px] italic">{title}</span>
      <div className="flex-1 h-px bg-border-sand" />
      <span className="text-[10px] uppercase tracking-[0.08em] font-medium text-ink-muted">
        {count}
      </span>
    </div>
  )
}
